import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { optimizeImage } from "@/lib/image";
import { effOf } from "@/lib/perms";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ArrowLeft, X } from "lucide-react";
import { money } from "@/lib/format";

const empty = {
  title: "", description: "", image_urls: [], total_numbers: 100, price_per_number: 1000,
  packages: [], draw_date: "", draw_method: "random", draw_rule_text: "",
  max_reshuffles: 5, reservation_minutes: 20, currency: "₡", rules_text: "",
  min_purchase: 1, max_purchase: "", winners_count: 3,
  sinpe_number: "", payment_info: "",
};

export default function RaffleForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const feat = effOf(user, null);
  const maxNums = user?.is_super_admin ? null : (feat.max_numbers_per_raffle ?? 100);
  const maxPhotos = user?.is_super_admin ? 20 : (feat.max_photos ?? 3);
  const { raffleId } = useParams();
  const editing = Boolean(raffleId);
  const [form, setForm] = useState(empty);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    if (editing) {
      (async () => {
        try {
          const res = await api.get(`/raffles/${raffleId}`);
          const d = res.data;
          const pkgs = (d.packages || []).map((p) => ({ ...p, _id: p._id || crypto.randomUUID() }));
          setForm({ ...empty, ...d, packages: pkgs, draw_date: d.draw_date ? d.draw_date.slice(0, 16) : "" });
        } catch (e) { toast.error("No se pudo cargar"); }
        finally { setLoading(false); }
      })();
    }
  }, [raffleId, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const uploadImages = async (files) => {
    const list = Array.from(files || []);
    const room = maxPhotos - form.image_urls.length;
    if (room <= 0) { toast.error(`Máximo ${maxPhotos} imágenes en tu plan`); return; }
    setUploadingImg(true);
    try {
      const added = [];
      for (const f of list.slice(0, room)) {
        const optimized = await optimizeImage(f);
        const fd = new FormData();
        fd.append("file", optimized);
        const res = await api.post("/raffles/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(BACKEND_URL + res.data.url);
      }
      set("image_urls", [...form.image_urls, ...added]);
      toast.success("Imagen(es) subida(s)");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al subir imagen");
    } finally { setUploadingImg(false); }
  };
  const removeImage = (url) => set("image_urls", form.image_urls.filter((u) => u !== url));
  const addPackage = () => set("packages", [...form.packages, { label: "", quantity: 4, _id: crypto.randomUUID() }]);
  const updatePackage = (i, k, v) => {
    const p = [...form.packages]; p[i] = { ...p[i], [k]: v }; set("packages", p);
  };
  const removePackage = (i) => set("packages", form.packages.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Ingresa un título");
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_numbers: Number(form.total_numbers),
        price_per_number: Number(form.price_per_number),
        max_reshuffles: Number(form.max_reshuffles),
        reservation_minutes: Number(form.reservation_minutes),
        min_purchase: Number(form.min_purchase) || 1,
        max_purchase: form.max_purchase ? Number(form.max_purchase) : null,
        winners_count: Number(form.winners_count) || 1,
        draw_date: form.draw_date ? new Date(form.draw_date).toISOString() : null,
        packages: form.packages.map((p) => ({ label: p.label || `${p.quantity} números`, quantity: Number(p.quantity), price: Number(p.quantity) * Number(form.price_per_number) })),
      };
      let id = raffleId;
      if (editing) await api.put(`/raffles/${raffleId}`, payload);
      else { const res = await api.post("/raffles", payload); id = res.data.id; }
      toast.success(editing ? "Rifa actualizada" : "Rifa creada");
      navigate(`/dashboard/raffles/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (loading) return <DashboardLayout title="Cargando..."><p className="text-neutral-400">Cargando...</p></DashboardLayout>;

  return (
    <DashboardLayout title={editing ? "Editar rifa" : "Nueva rifa"}>
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 rounded-xl -ml-2"><ArrowLeft size={16} className="mr-1" />Volver</Button>
      <div className="max-w-3xl space-y-6">
        <Section title="Información general">
          <div>
            <Label>Nombre del sorteo *</Label>
            <Input data-testid="raffle-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 rounded-lg" placeholder="Ej: Rifa iPhone 15 Pro" />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea data-testid="raffle-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-lg" rows={3} />
          </div>
          <div>
            <Label>Imágenes del premio (hasta {maxPhotos})</Label>
            <p className="text-xs text-neutral-400 mt-0.5 mb-2">Se mostrarán como carrusel en la página pública.</p>
            <div className="flex flex-wrap gap-3">
              {form.image_urls.map((u) => (
                <div key={u} className="relative w-24 h-24 rounded-xl overflow-hidden border border-neutral-200">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(u)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X size={12} /></button>
                </div>
              ))}
              {form.image_urls.length < maxPhotos && (
                <label data-testid="upload-image-label" className="w-24 h-24 rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#00AEEF] transition-colors text-neutral-400">
                  <input type="file" accept="image/*" multiple data-testid="raffle-image-input" className="hidden" disabled={uploadingImg}
                    onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }} />
                  {uploadingImg ? <span className="text-xs">Subiendo...</span> : <><Plus size={20} /><span className="text-xs mt-1">Agregar</span></>}
                </label>
              )}
            </div>
          </div>
        </Section>

        <Section title="Números y precios">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Cantidad total de números</Label>
              <Input data-testid="raffle-total" type="number" min={1} max={maxNums || undefined} value={form.total_numbers} onChange={(e) => set("total_numbers", e.target.value)} className="mt-1.5 rounded-lg" disabled={editing && form.status !== "draft"} />
              {maxNums != null && <p className="text-xs text-neutral-400 mt-1">Máximo permitido en tu cuenta: <b>{maxNums}</b>. Para ampliarlo, contacta al super-administrador.</p>}
            </div>
            <div>
              <Label>Precio por número</Label>
              <Input data-testid="raffle-price" type="number" min={0} value={form.price_per_number} onChange={(e) => set("price_per_number", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Combos / Paquetes</Label>
              <Button type="button" data-testid="add-package-btn" onClick={addPackage} variant="outline" size="sm" className="rounded-lg"><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <p className="text-xs text-neutral-400 mt-1">El precio se calcula automáticamente: cantidad × precio por número ({money(Number(form.price_per_number) || 0, form.currency)} c/u).</p>
            <div className="space-y-2 mt-2">
              {form.packages.map((p, i) => (
                <div key={p._id || i} className="flex gap-2 items-center" data-testid={`package-row-${i}`}>
                  <Input value={p.label} onChange={(e) => updatePackage(i, "label", e.target.value)} placeholder="Etiqueta (opcional)" className="rounded-lg flex-1" />
                  <Input type="number" min={1} value={p.quantity} onChange={(e) => updatePackage(i, "quantity", e.target.value)} placeholder="Cant." className="rounded-lg w-24" data-testid={`package-qty-${i}`} />
                  <div data-testid={`package-price-${i}`} className="w-28 h-10 flex items-center justify-end px-3 rounded-lg bg-neutral-100 text-sm font-semibold text-neutral-700 whitespace-nowrap overflow-hidden">
                    {money((Number(p.quantity) || 0) * (Number(form.price_per_number) || 0), form.currency)}
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removePackage(i)} className="text-red-500"><Trash2 size={16} /></Button>
                </div>
              ))}
              {form.packages.length === 0 && <p className="text-sm text-neutral-400">Ej: un combo de 4 números. El precio se suma solo según el precio de la rifa. Opcional.</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Compra mínima por persona</Label>
              <Input data-testid="raffle-min" type="number" min={1} value={form.min_purchase} onChange={(e) => set("min_purchase", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label>Compra máxima (opcional)</Label>
              <Input data-testid="raffle-max" type="number" min={1} value={form.max_purchase} onChange={(e) => set("max_purchase", e.target.value)} placeholder="Sin límite" className="mt-1.5 rounded-lg" />
            </div>
          </div>
        </Section>

        <Section title="Método de pago">
          <p className="text-sm text-neutral-500 -mt-1 mb-3">Estos datos se muestran al comprador en el landing de la rifa, con un botón para copiar el número.</p>
          <div>
            <Label>SINPE Móvil — número de teléfono</Label>
            <Input data-testid="raffle-sinpe" value={form.sinpe_number} onChange={(e) => set("sinpe_number", e.target.value)} className="mt-1.5 rounded-lg" placeholder="Ej: 8888-8888" />
            <p className="text-xs text-neutral-400 mt-1">Número al que el comprador hará el depósito por SINPE Móvil. El comprador podrá copiarlo con un toque.</p>
          </div>
          <div className="mt-4">
            <Label>Instrucciones de pago adicionales (opcional)</Label>
            <Textarea data-testid="raffle-payment-info" value={form.payment_info} onChange={(e) => set("payment_info", e.target.value)} className="mt-1.5 rounded-lg" rows={3} placeholder={"Ej: SINPE Móvil a nombre de María Rodríguez.\nEnvía el comprobante para validar tu compra."} />
          </div>
        </Section>

        <Section title="Sorteo y reglas">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha y hora de cierre</Label>
              <Input data-testid="raffle-draw-date" type="datetime-local" value={form.draw_date} onChange={(e) => set("draw_date", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label>Método del ganador</Label>
              <Select value={form.draw_method} onValueChange={(v) => set("draw_method", v)}>
                <SelectTrigger data-testid="raffle-draw-method" className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Aleatorio verificable</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="external">Vinculado a Lotería Nacional</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Cantidad de números premiados</Label>
            <Select value={String(form.winners_count)} onValueChange={(v) => set("winners_count", Number(v))}>
              <SelectTrigger data-testid="raffle-winners-count" className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 número premiado</SelectItem>
                <SelectItem value="2">2 números premiados (1º y 2º)</SelectItem>
                <SelectItem value="3">3 números premiados (1º, 2º y 3º)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-400 mt-1">Al sortear se elegirán esta cantidad de ganadores por orden de lugar.</p>
          </div>
          <div className={form.draw_method === "external" ? "" : "hidden"}>
            <Label>Regla de combinación (Lotería Nacional)</Label>
            <Textarea data-testid="raffle-draw-rule" value={form.draw_rule_text} onChange={(e) => set("draw_rule_text", e.target.value)} className="mt-1.5 rounded-lg" rows={2} placeholder="Ej: Las últimas 3 cifras del premio mayor de la Lotería Nacional del domingo." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Reintentos de selección (reshuffles)</Label>
              <Input data-testid="raffle-reshuffles" type="number" min={0} value={form.max_reshuffles} onChange={(e) => set("max_reshuffles", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label>Minutos de reserva</Label>
              <Input data-testid="raffle-reservation" type="number" min={1} value={form.reservation_minutes} onChange={(e) => set("reservation_minutes", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
          </div>
          <div>
            <Label>Reglas adicionales (texto libre)</Label>
            <Textarea data-testid="raffle-rules" value={form.rules_text} onChange={(e) => set("rules_text", e.target.value)} className="mt-1.5 rounded-lg" rows={2} placeholder="Ej: Bonificación de 1 número extra por compras mayores a ₡10.000." />
          </div>
        </Section>

        <div className="flex gap-3">
          <Button data-testid="save-raffle-btn" onClick={submit} disabled={saving} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl h-11 px-6">
            {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear rifa (borrador)"}
          </Button>
          <Button variant="outline" onClick={() => navigate(-1)} className="rounded-xl h-11">Cancelar</Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)] space-y-4">
      <h2 className="font-display font-bold text-lg">{title}</h2>
      {children}
    </div>
  );
}
